"use client";
import React from 'react';
import { Check, Sparkles, Shield, MessageSquare, LineChart, Zap, Timer, Target } from 'lucide-react';

export default function FleetAiPlansPage() {
  const [submitting, setSubmitting] = React.useState<string | null>(null);
  const [toast, setToast] = React.useState<string | null>(null);

  const subscribe = async (planLookupKey: string) => {
    try {
      setSubmitting(planLookupKey);
      setToast(null);
      const r = await fetch('/api/fleet/billing/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planLookupKey }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j?.url) {
        window.location.href = j.url as string;
        return;
      }
      setToast(j?.error || 'Unable to start subscription. Please contact support.');
    } catch (e: any) {
      setToast(e?.message || 'Something went wrong.');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6" style={{ color: 'var(--fleet-text)' }}>
      <div className="rounded-lg border p-4 md:p-6 overflow-hidden relative" style={{ borderColor: 'var(--fleet-border)', background: 'linear-gradient(180deg, rgba(99,102,241,0.10), rgba(0,0,0,0))' }}>
        <div className="absolute -right-12 -top-12 opacity-20" aria-hidden>
          <Sparkles size={160} />
        </div>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative">
          <div>
            <div className="text-[24px] md:text-[28px] font-semibold">Strix AI</div>
            <div className="text-[13px] md:text-[14px] mt-2" style={{ color: 'var(--fleet-muted)' }}>
              Win more jobs with faster quotes. Strix AI suggests prices, writes polished replies, and flags what needs attention —
              so you respond first and look professional every time. Teams using Fleet AI close work faster, spend less time typing,
              and keep customers in the loop.
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
              <span className="px-2 py-0.5 rounded-full" style={{ background: 'rgba(99,102,241,0.16)', border: '1px solid rgba(99,102,241,0.35)', color: '#a5b4fc' }}>
                Acts like a teammate, not a toy
              </span>
              <span className="px-2 py-0.5 rounded-full" style={{ background: 'rgba(250,204,21,0.16)', border: '1px solid rgba(250,204,21,0.35)', color: '#fde68a' }}>
                Faster quotes → higher win‑rates
              </span>
            </div>
          </div>
          <div className="text-[11px] px-2 py-1 rounded border" style={{ border: '1px solid var(--fleet-border)', background: 'rgba(255,255,255,0.04)' }}>
            Add‑on to any Fleet plan
          </div>
        </div>
      </div>

      {/* Results band */}
      <div className="rounded-lg border p-4 md:p-5" style={{ borderColor: 'var(--fleet-border)', background: 'linear-gradient(180deg, rgba(34,197,94,0.07), rgba(0,0,0,0))' }}>
        <div className="text-[13px] font-semibold mb-2">Results you can expect</div>
        <div className="grid md:grid-cols-3 gap-3 text-[12px]" style={{ color: 'var(--fleet-text)' }}>
          <div className="rounded border p-3" style={{ borderColor: 'var(--fleet-border)', background: 'rgba(255,255,255,0.02)' }}>
            <div className="font-medium">2–4× faster quote turnaround</div>
            <div className="mt-1" style={{ color: 'var(--fleet-muted)' }}>
              Move to the front of the queue with rapid, budget‑aware suggestions.
            </div>
          </div>
          <div className="rounded border p-3" style={{ borderColor: 'var(--fleet-border)', background: 'rgba(255,255,255,0.02)' }}>
            <div className="font-medium">10–25% higher acceptance rates</div>
            <div className="mt-1" style={{ color: 'var(--fleet-muted)' }}>
              Clear, professional comms reduce drop‑offs and increase wins.
            </div>
          </div>
          <div className="rounded border p-3" style={{ borderColor: 'var(--fleet-border)', background: 'rgba(255,255,255,0.02)' }}>
            <div className="font-medium">30–50% fewer revisions</div>
            <div className="mt-1" style={{ color: 'var(--fleet-muted)' }}>
              Better first responses mean fewer back‑and‑forth messages.
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div className="rounded border p-3 text-[12px]" style={{ borderColor: 'var(--fleet-border)', background: 'color-mix(in srgb, var(--fleet-amber) 10%, transparent)' }}>
          {toast}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-3">
        <div className="rounded border p-3 flex items-start gap-2" style={{ borderColor: 'var(--fleet-border)', background: 'rgba(255,255,255,0.02)' }}>
          <Timer size={16} />
          <div className="text-[12px]">
            <div className="font-medium">Respond first</div>
            <div className="mt-0.5" style={{ color: 'var(--fleet-muted)' }}>
              Rapid, budget‑aware quote suggestions help you reply in minutes—not hours—so you’re top of the pile.
            </div>
          </div>
        </div>
        <div className="rounded border p-3 flex items-start gap-2" style={{ borderColor: 'var(--fleet-border)', background: 'rgba(255,255,255,0.02)' }}>
          <Target size={16} />
          <div className="text-[12px]">
            <div className="font-medium">Win more work</div>
            <div className="mt-0.5" style={{ color: 'var(--fleet-muted)' }}>
              Clear, professional messages reduce friction and increase acceptance—without overshooting budgets.
            </div>
          </div>
        </div>
        <div className="rounded border p-3 flex items-start gap-2" style={{ borderColor: 'var(--fleet-border)', background: 'rgba(255,255,255,0.02)' }}>
          <Zap size={16} />
          <div className="text-[12px]">
            <div className="font-medium">Operate with clarity</div>
            <div className="mt-0.5" style={{ color: 'var(--fleet-muted)' }}>
              Live Ops summaries surface risks, delays, and next steps so your dispatch stays ahead of issues.
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[14px] font-semibold">Choose your AI plan</div>
        <div className="text-[11px]" style={{ color: 'var(--fleet-muted)' }}>
          Prices ex VAT • Cancel anytime
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* AI Core */}
        <div className="relative rounded border p-4 overflow-hidden" style={{ borderColor: 'var(--fleet-border)', background: 'rgba(255,255,255,0.02)' }}>
          <div className="text-[13px] font-semibold">AI Core</div>
          <div className="text-[11px] mt-1" style={{ color: 'var(--fleet-muted)' }}>For teams getting started with AI</div>
          <div className="mt-3 flex items-end gap-2">
            <div className="text-[22px] font-semibold">£39</div>
            <div className="text-[11px]" style={{ color: 'var(--fleet-muted)' }}>/month</div>
          </div>
          <div className="mt-2 text-[11px]" style={{ color: 'var(--fleet-muted)' }}>Includes 150,000 tokens/month</div>
          <div className="mt-3 space-y-1 text-[12px]">
            <div className="flex items-center gap-2"><Check size={14} /> Reply in minutes with smart price suggestions</div>
            <div className="flex items-center gap-2"><Check size={14} /> Send polished, professional replies in a click</div>
            <div className="flex items-center gap-2"><Check size={14} /> Get quick Live Ops summaries when you need them</div>
            <div className="flex items-center gap-2"><Check size={14} /> Keep comms clear and consistent across the team</div>
            <div className="flex items-center gap-2"><Check size={14} /> Apply with one tap inside your workflow</div>
          </div>
          <div className="mt-4">
            <button
              className="px-3 py-1.5 text-[12px] rounded w-full md:w-auto"
              style={{ background: 'var(--fleet-accent)', color: '#000' }}
              onClick={() => subscribe('ai_core')}
              disabled={submitting === 'ai_core'}
            >
              {submitting === 'ai_core' ? 'Loading…' : 'Get AI Core'}
            </button>
          </div>
        </div>

        {/* AI Scale */}
        <div className="relative rounded border p-4 overflow-hidden" style={{ borderColor: 'var(--fleet-border)', background: 'linear-gradient(180deg, rgba(99,102,241,0.08), rgba(0,0,0,0))', boxShadow: '0 14px 36px rgba(99,102,241,0.18)' }}>
          <div className="absolute right-3 top-3 text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(99,102,241,0.16)', border: '1px solid rgba(99,102,241,0.4)', color: '#a5b4fc' }}>Most popular</div>
          <div className="text-[13px] font-semibold">AI Scale</div>
          <div className="text-[11px] mt-1" style={{ color: 'var(--fleet-muted)' }}>For larger teams and heavy usage</div>
          <div className="mt-3 flex items-end gap-2">
            <div className="text-[22px] font-semibold">£199</div>
            <div className="text-[11px]" style={{ color: 'var(--fleet-muted)' }}>/month</div>
          </div>
          <div className="mt-2 text-[11px]" style={{ color: 'var(--fleet-muted)' }}>Includes 2,000,000 tokens/month</div>
          <div className="mt-3 space-y-1 text-[12px]">
            <div className="flex items-center gap-2"><Check size={14} /> Everything in AI Core</div>
            <div className="flex items-center gap-2"><Check size={14} /> Handle heavy quoting and batch drafts at speed</div>
            <div className="flex items-center gap-2"><Check size={14} /> Deep‑dive Live Ops with on‑demand summaries</div>
            <div className="flex items-center gap-2"><Check size={14} /> Priority capacity for busy periods</div>
            <div className="flex items-center gap-2"><Check size={14} /> Priority support</div>
          </div>
          <div className="mt-4">
            <button
              className="px-3 py-1.5 text-[12px] rounded w-full md:w-auto"
              style={{ background: 'linear-gradient(90deg, #a5b4fc, #34d399)', color: '#0f172a' }}
              onClick={() => subscribe('ai_scale')}
              disabled={submitting === 'ai_scale'}
            >
              {submitting === 'ai_scale' ? 'Loading…' : 'Get AI Scale'}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border p-4 md:p-5" style={{ borderColor: 'var(--fleet-border)', background: 'rgba(255,255,255,0.02)' }}>
        <div className="text-[13px] font-semibold mb-2">How Fleet AI helps your team</div>
        <div className="grid md:grid-cols-3 gap-3 text-[12px]" style={{ color: 'var(--fleet-text)' }}>
          <div className="rounded border p-3" style={{ borderColor: 'var(--fleet-border)' }}>
            <div className="flex items-center gap-2 font-medium"><MessageSquare size={14} /> Quotes & comms</div>
            <div className="mt-1" style={{ color: 'var(--fleet-muted)' }}>
              Suggests prices within budget and drafts clear, on‑brand messages for customers and partners.
            </div>
          </div>
          <div className="rounded border p-3" style={{ borderColor: 'var(--fleet-border)' }}>
            <div className="flex items-center gap-2 font-medium"><LineChart size={14} /> Live Ops awareness</div>
            <div className="mt-1" style={{ color: 'var(--fleet-muted)' }}>
              Summarizes routes, risks, and delays. Highlights SLA/ETA risk with crisp next steps.
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border p-4 md:p-5" style={{ borderColor: 'var(--fleet-border)', background: 'rgba(255,255,255,0.02)' }}>
        <div className="text-[13px] font-semibold mb-2">Why teams upgrade to Fleet AI</div>
        <div className="grid md:grid-cols-4 gap-3 text-[12px]" style={{ color: 'var(--fleet-text)' }}>
          <div className="rounded border p-3" style={{ borderColor: 'var(--fleet-border)' }}>
            <div className="font-medium">Win more work</div>
            <div className="mt-1" style={{ color: 'var(--fleet-muted)' }}>
              Be first to respond with clear pricing and a professional message.
            </div>
          </div>
          <div className="rounded border p-3" style={{ borderColor: 'var(--fleet-border)' }}>
            <div className="font-medium">Save hours every week</div>
            <div className="mt-1" style={{ color: 'var(--fleet-muted)' }}>
              Less typing and fewer back‑and‑forths on quotes and updates.
            </div>
          </div>
          <div className="rounded border p-3" style={{ borderColor: 'var(--fleet-border)' }}>
            <div className="font-medium">Stay consistent</div>
            <div className="mt-1" style={{ color: 'var(--fleet-muted)' }}>
              Every reply sounds sharp and on‑brand, whoever sends it.
            </div>
          </div>
          <div className="rounded border p-3" style={{ borderColor: 'var(--fleet-border)' }}>
            <div className="font-medium">See risks sooner</div>
            <div className="mt-1" style={{ color: 'var(--fleet-muted)' }}>
              Real‑time summaries highlight what matters so nothing slips.
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border p-4 md:p-5" style={{ borderColor: 'var(--fleet-border)', background: 'rgba(255,255,255,0.02)' }}>
        <div className="text-[13px] font-semibold mb-2">How it works</div>
        <div className="grid md:grid-cols-2 gap-3 text-[12px]" style={{ color: 'var(--fleet-text)' }}>
          <div className="rounded border p-3" style={{ borderColor: 'var(--fleet-border)' }}>
            <div className="font-medium">1) Work where you are</div>
            <div className="mt-1" style={{ color: 'var(--fleet-muted)' }}>
              Use AI in Quotes, Jobs, and Live Ops. Provide context; get price suggestions, drafts, and summaries instantly.
            </div>
          </div>
          <div className="rounded border p-3" style={{ borderColor: 'var(--fleet-border)' }}>
            <div className="font-medium">2) Review and apply</div>
            <div className="mt-1" style={{ color: 'var(--fleet-muted)' }}>
              One‑tap apply with a human in the loop for speed and consistency.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


